import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { lastValueFrom, map } from 'rxjs';

import { BaseApi } from '@core/services/api/base.api';
import { KeyringTenant, KeyringTenantResponse } from '@core/models/api/keyring/tenant.model';
import { problemSlug } from '@shared/helpers/api-error.helper';
import { getLinkFromId } from '@shared/helpers/link.helper';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { DEFAULT_QUERY_OPTIONS } from '@shared/helpers/query.helper';

function isClientError(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status >= 400 && error.status < 500;
}

/**
 * Why the last tenant request failed. `status` is the HTTP status, 0 when the API could not be reached;
 * `problem` is the slug of the API's problem type (`account-not-active`, ...) when it sent one.
 */
export interface TenantLoadError {
    status: number;
    problem?: string;
}

@Injectable({ providedIn: 'root' })
export class TenantApi extends BaseApi {
    /** Reactive tenant signal — updates when tenant data is fetched */
    public readonly tenant = signal<KeyringTenant | undefined>(undefined);
    public readonly rootId = computed(() => this.tenant()?.rootId);
    /** Set when the last `getOne` failed, cleared when it succeeds. Keeps a failed request apart from "no tenant". */
    public readonly loadError = signal<TenantLoadError | undefined>(undefined);

    private readonly endpoints = {
        many: `${this.baseUrl}/tenants`,
        one: `${this.baseUrl}/tenants/:id`,
    };
    private readonly tenantQueryKey  = ['tenant'];
    private readonly queryClient = inject(QueryClient);

    public getTenant(): KeyringTenant | undefined {
        return this.queryClient.getQueryData<KeyringTenant>(this.tenantQueryKey);
    }

    public async getOne(tenantId: string): Promise<KeyringTenant> {
        let tenant: KeyringTenant;
        try {
            tenant = await this.queryClient.fetchQuery<KeyringTenant>({
                queryKey: [...this.tenantQueryKey, tenantId],
                queryFn: () => lastValueFrom(
                    this.get<KeyringTenantResponse>(this.endpoints.one.replace(':id', String(tenantId))).pipe(
                        map(t => this.parseTenant(t))
                    )
                ),
                ...DEFAULT_QUERY_OPTIONS,
                // A 4xx (expired session, wrong audience, unknown tenant) does not heal on a second try
                retry: (failureCount, error) => !isClientError(error) && failureCount < DEFAULT_QUERY_OPTIONS.retry,
            });
        } catch (error) {
            this.loadError.set({
                status: error instanceof HttpErrorResponse ? error.status : 0,
                problem: problemSlug(error),
            });
            throw error;
        }
        this.loadError.set(undefined);
        // Also cache at the base key so getTenant() can find it
        this.queryClient.setQueryData(this.tenantQueryKey, tenant);
        this.tenant.set(tenant);
        return tenant;
    }

    private parseTenant(keyringFile: KeyringTenantResponse): KeyringTenant {
        const rootId = keyringFile.rootId;

        return {
            name:             keyringFile.name,
            domain:           keyringFile.domain,
            rootId:           rootId,
            createdBy:        keyringFile.createdBy,
            modifiedBy:       keyringFile.modifiedBy,
            creationDate:     keyringFile.creationDate,
            modificationDate: keyringFile.modificationDate,
            _links:           {
                self:   keyringFile._links.self,
                root:   getLinkFromId(rootId, this.baseUrl),
            },
        };
    }
}
